import React, { useEffect, useRef, useState, useCallback } from "react";
import { Warning } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";

const IDLE_MS = 5 * 60 * 1000; // 5 menit
const WARN_MS = 30 * 1000;     // 30 detik warning

/**
 * Hook auto-logout: setelah 4:30 tidak ada aktivitas → tampilkan warning modal 30 detik.
 * Kalau tidak diperpanjang, logout otomatis.
 * onLogout: async function to run when idle expires
 */
export function useIdleLogout(onLogout, enabled = true) {
  const [warnOpen, setWarnOpen] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const idleTimer = useRef(null);
  const warnTimer = useRef(null);
  const countdownInterval = useRef(null);

  const clearAll = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (warnTimer.current) clearTimeout(warnTimer.current);
    if (countdownInterval.current) clearInterval(countdownInterval.current);
  }, []);

  const doLogout = useCallback(() => {
    clearAll();
    setWarnOpen(false);
    onLogout?.();
  }, [clearAll, onLogout]);

  const openWarning = useCallback(() => {
    setWarnOpen(true);
    setCountdown(30);
    if (countdownInterval.current) clearInterval(countdownInterval.current);
    countdownInterval.current = setInterval(() => {
      setCountdown((c) => (c <= 1 ? 0 : c - 1));
    }, 1000);
    if (warnTimer.current) clearTimeout(warnTimer.current);
    warnTimer.current = setTimeout(doLogout, WARN_MS);
  }, [doLogout]);

  const reset = useCallback(() => {
    if (!enabled) return;
    clearAll();
    setWarnOpen(false);
    idleTimer.current = setTimeout(openWarning, IDLE_MS - WARN_MS);
  }, [enabled, clearAll, openWarning]);

  useEffect(() => {
    if (!enabled) { clearAll(); return; }
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    const handler = () => { if (!warnOpen) reset(); };
    events.forEach((ev) => window.addEventListener(ev, handler, { passive: true }));
    reset();
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, handler));
      clearAll();
    };
  }, [enabled, reset, clearAll, warnOpen]);

  const extend = () => reset();

  const Modal = () => warnOpen ? (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" data-testid="idle-warn-modal">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-6 border border-border">
        <div className="flex items-start gap-4 mb-4">
          <div className="h-12 w-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
            <Warning size={24} weight="duotone" className="text-amber-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-lg font-black text-foreground">Anda akan keluar otomatis</h3>
            <p className="text-sm text-muted-foreground mt-1">Tidak ada aktivitas selama beberapa menit. Sesi akan berakhir dalam <b className="text-amber-600 font-mono">{countdown} detik</b>.</p>
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-4">
          <Button variant="outline" onClick={doLogout} data-testid="idle-logout-now">Keluar Sekarang</Button>
          <Button onClick={extend} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="idle-extend">Perpanjang Sesi</Button>
        </div>
      </div>
    </div>
  ) : null;

  return { Modal, extend };
}
